// ================================================================================================
import _ from "lodash";
import { Component, CSSProperties, Fragment, ReactElement } from 'react';
import Checkbox from '@Primitive/Checkbox';
import TextBox from '@Primitive/TextBox';
import { SketchPicker } from 'react-color';
import DatePicker from 'rsuite/DatePicker';
import moment from 'moment';
import { getBool, getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { Select, SelectOption, SelectOptions } from './Select';
import RsDateRangePicker from '@Primitive/RsDateRangePicker';
import { DateFormat, Formatters } from '@Utils/Formats';
import { Localization } from './Localization/Localization';
import { DebugEx } from '@Utils/DebugEx';
import { GetQueryBuilderLocalizationDictionary, QueryBuilderLocalizationTextMapKey, QueryBuilderUILabelKey } from './QueryBuilderLocalization';
import Toggle from '@Primitive/Toggle';
import { IconButton } from '@Primitive/Button';
import { green200, blue200, red200 } from '@Utils/Colors';
import Popover from '@Primitive/Popover';
// ================================================================================================
interface QueryBuilderProps {
    width?: number;
    height?: number;
    QueryStructure: QueryStructure[];
    Fields: Field[];
    DefaultOperators: { [key: string]: Operator[] };
    Combinators: Combinator[];
    TimePickerEnable: boolean;
    onQueryStructureChange?: (struct: QueryStructure[]) => void;
    updateOutputPort?: (text: string, json: QueryStructure[]) => void;
    HideToolbar?: boolean;
    localizationKey?: string;
}
// ------------------------------------------------------------------------------------------------
// todo: remove cloned fields
export interface QueryBuilderState {
    QueryStructure: QueryStructure[];
}
// ---------------------------------------------------------------------------------------------------
export interface QueryStructure {
    id: string;
    rules: QueryStructure[];
    type: QueryItemType;
    notEnabled?: boolean;
    combinator?: string;
    field?: string;
    value?: any;
    operator?: string;
    readonlyStructure?: boolean;
}
// ---------------------------------------------------------------------------------------------------
export interface Operator {
    key: string;
    label: string;
}
// ---------------------------------------------------------------------------------------------------
export interface Combinator {
    key: string;
    label: string;
}
// ---------------------------------------------------------------------------------------------------
export interface Field {
    key: string;
    label: string;
    type: FieldsItemType;
    useDefaultOperator: boolean;
    operators: Operator[];
    values?: { key: string, label: string }[];
}
// ---------------------------------------------------------------------------------------------------
export enum QueryItemType {
    Group = 0,
    Rule = 1
}
// ---------------------------------------------------------------------------------------------------
export enum FieldsItemType {
    Text = 0,
    Number = 1,
    Date = 2,
    Boolean = 3,
    Color = 4,
    Select = 5
}
// ---------------------------------------------------------------------------------------------------
export class QueryBuilder extends Component<QueryBuilderProps, QueryBuilderState> {
    // ---------------------------------------------------------------------------------------------------
    public static defaultProps: QueryBuilderProps = {
        QueryStructure: [],
        Fields: [],
        DefaultOperators: {},
        Combinators: [],
        TimePickerEnable: false,
        onQueryStructureChange: null,
        updateOutputPort: null,
        HideToolbar: null,
        localizationKey: '',
        width: null,
        height: null
    }
    // ---------------------------------------------------------------------------------------------------
    private static _now: moment.Moment;
    private _fieldsCache: { [name: string]: Field } = {};
    private _query: string;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: QueryBuilderProps) {
        super(props);

        if (!isDefined(QueryBuilder._now))
            QueryBuilder._now = moment().locale('en-gb').utcOffset(0);

        _.forEach(this.props.Fields, element => this._fieldsCache[element.key] = element);
        this.state = { ...this._getStateFromProps(props) };
    }
    // ---------------------------------------------------------------------------------------------------
    public render() {
        const { width, height } = this.props;
        const { QueryStructure } = this.state;

        return <div
            style={{
                width: width,
                height: height
            }}>
            <div
                ref={r => {
                    if (r != null)
                        r.style.paddingRight = r.scrollHeight > r.clientHeight ? '1px' : '0px';
                }}
                style={{
                    width: width,
                    height: height,
                    overflowY: 'auto'
                }}>
                <div>{this._renderQueryStructure(QueryStructure, [], 1)}</div>
            </div>
        </div>;
    }
    // ---------------------------------------------------------------------------------------------------
    public componentDidUpdate(prevProps: QueryBuilderProps) {
        if (!_.isEqual(this.props.QueryStructure, prevProps.QueryStructure) ||
            !_.isEqual(this.props.Fields, prevProps.Fields)) {
            this.setState(this._getStateFromProps(this.props));
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _getStateFromProps(nextProps: QueryBuilderProps): { QueryStructure: QueryStructure[] } {
        // Update dictionary before setting state as `FieldDictionary` is used for rendering
        this._fieldsCache = {};
        _.forEach(nextProps.Fields, element => this._fieldsCache[element.key] = element);
        return { QueryStructure: _.clone(nextProps.QueryStructure) };
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderQueryStructure(struct: QueryStructure[], items: ReactElement[], level: number): ReactElement[] {
        this._updateQueryText();

        const indent = (level - 1) * 20;
        const rootStyle: CSSProperties = {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2
        };

        const firstFlexItemStyle: CSSProperties = {
            display: 'flex',
            alignItems: 'center'
        };
        if (level > 1)
            firstFlexItemStyle.width = 'calc(100% - 40px)';

        const locMap = QueryBuilder.LocalizeDictionary(this.props.localizationKey);
        _.forEach(struct, (v, i) => {

            const readonly = getBool(v.readonlyStructure);
            if (v.type == QueryItemType.Group) {

                const hasRules = _.isArray(v.rules) && v.rules.length > 0;
                items.push(<div key={v.id} style={{
                    ...rootStyle,
                    marginLeft: indent
                }}>
                    <div style={firstFlexItemStyle}>
                        <span>NOT</span>
                        <Checkbox
                            style={{ marginLeft: 4 }}
                            value={v.notEnabled}
                            onClick={readonly ? undefined : () => {
                                // Update the negation of element.
                                v.notEnabled = !v.notEnabled;
                                this.forceUpdate(this._onStructureChange);
                            }} />
                        {/* -------------------------------------------------- */}
                        <Select
                            key={'rule-combinator-' + v.id}
                            isDisabled={readonly}
                            isMulti={false}
                            isClearable={false}
                            containerStyle={{ margin: '0 4px' }}
                            options={this._renderCombinators()}
                            valueStr={v.combinator}
                            onChange={value => {
                                // Update the combinator of element.
                                v.combinator = value;
                                this.forceUpdate(this._onStructureChange);
                            }} />
                        {/* -------------------------------------------------- */}
                        {!readonly
                            ? <IconButton
                                key={'add-group-' + v.id}
                                tooltip={locMap[QueryBuilderUILabelKey.Add_Group]}
                                iconClassName='fas fa-layer-plus'
                                iconStyle={{ color: green200 }}
                                onClick={() => {
                                    const combinators = this.props.Combinators;
                                    if (!_.isArray(combinators) || combinators.length == 0)
                                        return;
                                    // Add group element without rules.
                                    if (!_.isArray(v.rules))
                                        v.rules = [];
                                    v.rules.push({
                                        id: getRandomSmallKey(),
                                        type: QueryItemType.Group,
                                        combinator: combinators[0].key,
                                        rules: []
                                    });
                                    this.forceUpdate(this._onStructureChange);
                                }} />
                            : null}
                        {/* -------------------------------------------------- */}
                        {!readonly
                            ? <IconButton
                                key={'add-rule-' + v.id}
                                tooltip={locMap[QueryBuilderUILabelKey.Add_Rule]}
                                iconClassName='fas fa-plus'
                                iconStyle={{ color: green200 }}
                                onClick={() => {
                                    const fields = this.props.Fields;
                                    if (!_.isArray(fields) || fields.length == 0)
                                        return;

                                    const field = fields[0];

                                    // Define the available operators.
                                    let operators: Operator[] = [];
                                    if (field.useDefaultOperator)
                                        operators = this
                                            .props
                                            .DefaultOperators[FieldsItemType[field.type]];
                                    else
                                        operators = field.operators;

                                    if (!_.isArray(operators) || operators.length == 0)
                                        return;

                                    // Add rule element.
                                    if (!_.isArray(v.rules))
                                        v.rules = [];
                                    v.rules.push(
                                        {
                                            id: getRandomSmallKey(),
                                            type: QueryItemType.Rule,
                                            operator: operators[0].key,
                                            field: field.key,
                                            rules: [],
                                            value: null
                                        });
                                    this.forceUpdate(this._onStructureChange);
                                }} />
                            : null}
                        {/* -------------------------------------------------- */}
                        {level == 1
                            ? <IconButton
                                key='clear-all'
                                tooltip={locMap[QueryBuilderUILabelKey.Clear_All]}
                                iconClassName='fas fa-align-slash'
                                iconStyle={{ color: red200 }}
                                onClick={() => this.setState(s => {
                                    // Clear all elements except the root.
                                    s.QueryStructure[0].rules = [];
                                    return s;
                                }, this._onStructureChange)} />
                            : null}
                        {/* -------------------------------------------------- */}
                        {level == 1 && this.props.HideToolbar != true
                            ? <Fragment>
                                <IconButton
                                    key='query-string'
                                    onClick={undefined}
                                    tooltip={this._query}
                                    iconClassName='fas fa-question-circle'
                                    iconStyle={{
                                        cursor: 'initial',
                                        color: blue200
                                    }} />
                                <IconButton
                                    key='query-structure-out'
                                    tooltip={locMap[QueryBuilderUILabelKey.Update_Query]}
                                    iconClassName='fas fa-play-circle'
                                    iconStyle={{ color: blue200 }}
                                    onClick={this._onSendOutClick} />
                            </Fragment>
                            : null}
                    </div>
                    {/* -------------------------------------------------- */}
                    <div>
                        {level > 1
                            ? <IconButton
                                key={'delete-group-' + v.id}
                                disabled={readonly}
                                tooltip={locMap[QueryBuilderUILabelKey.Delete]}
                                iconClassName='fas fa-trash'
                                iconStyle={{ color: red200 }}
                                onClick={() => {
                                    // Delete group element.
                                    struct.splice(i, 1);
                                    this.forceUpdate(this._onStructureChange);
                                }} />
                            : null}
                    </div>
                </div>);
                if (hasRules)
                    this._renderQueryStructure(v.rules, items, level + 1);
            }
            else if (v.type == QueryItemType.Rule) {

                items.push(<div key={v.id} style={{
                    ...rootStyle,
                    paddingLeft: 20,
                    marginLeft: indent - 20
                }}>
                    <div style={firstFlexItemStyle}>
                        <Select
                            key={'rule-field-' + v.id}
                            isDisabled={readonly}
                            isMulti={false}
                            isClearable={false}
                            containerStyle={{ width: '100%', margin: '0 4px' }}
                            options={_.map(this.props.Fields, field => ({
                                label: field.label,
                                value: field.key
                            } as SelectOption)) as SelectOptions}
                            valueStr={v.field}
                            onChange={value => {
                                v.field = value;
                                const field = this._fieldsCache[value];
                                if (!isDefined(field))
                                    return;

                                if (field.type == FieldsItemType.Color)
                                    v.value = 'rgba(255,255,255,1)';

                                if (_.isArray(field.operators) && field.operators.length > 0)
                                    v.operator = field.operators[0].key;

                                this.forceUpdate(this._onStructureChange);
                            }} />
                        {/* -------------------------------------------------- */}
                        <Select
                            key={'rule-operator-' + v.id}
                            isDisabled={readonly}
                            isMulti={false}
                            isClearable={false}
                            containerStyle={{ width: '100%', margin: '0 4px' }}
                            options={this._renderOperators(v)}
                            valueStr={v.operator}
                            onChange={value => {
                                const field = this._fieldsCache[v.field];
                                if (!isDefined(field))
                                    return;

                                v.operator = value;
                                if (field.type == FieldsItemType.Date && isDefined(v.value))
                                    v.value = null;

                                this.forceUpdate(this._onStructureChange);
                            }} />
                        {/* -------------------------------------------------- */}
                        {this._renderValueElement('group-' + level + '-element-' + i, v)}
                    </div>
                    {/* -------------------------------------------------- */}
                    <div>
                        <IconButton
                            key={'delete-rule-' + v.id}
                            disabled={readonly}
                            tooltip={locMap[QueryBuilderUILabelKey.Delete]}
                            iconClassName='fas fa-trash'
                            iconStyle={{ color: red200 }}
                            onClick={() => {
                                // Delete rule element.
                                struct.splice(i, 1);
                                this.forceUpdate(this._onStructureChange);
                            }} />
                    </div>
                </div>);
            }
        });
        return items;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderCombinators(): SelectOptions {
        return _.map(this.props.Combinators, element => ({
            value: element.key,
            label: element.label
        } as SelectOption));
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderOperators(element: QueryStructure): SelectOptions {
        // try get field 
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return;

        if (field.useDefaultOperator)
            return _.map(this.props.DefaultOperators[FieldsItemType[field.type]], operator => ({
                value: operator.key,
                label: operator.label
            } as SelectOption));
        else
            return _.map(field.operators, operator => ({
                value: operator.key,
                label: operator.label
            } as SelectOption));
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderSelectFieldItems(element: QueryStructure): SelectOptions {
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return null;
        return _.map(field.values, field => ({
            value: field.key,
            label: field.label
        } as SelectOption));
    }
    // ---------------------------------------------------------------------------------------------------
    private _isNullOperator(operator: string): boolean {
        return operator.toLowerCase().indexOf('null') != -1;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderValueElement(elementId: string, element: QueryStructure): ReactElement {
        // check operator
        if (!isDefined(element.operator))
            return null;

        if (this._isNullOperator(element.operator))
            return null;

        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return null;

        const type = field.type;
        switch (type) {
            case FieldsItemType.Text:
            case FieldsItemType.Number:
                return <TextBox
                    fullWidth
                    type={type == FieldsItemType.Number ? "number" : "text"}
                    key={'text-field-value-' + element.id}
                    value={isDefined(element.value) ? element.value : ""}
                    onChangeString={newValue => {
                        // Update the value of element.
                        element.value = type == FieldsItemType.Number ? parseFloat(newValue) : newValue;
                        this.forceUpdate(this._onStructureChange);
                    }}
                />;
            case FieldsItemType.Boolean:
                return <Toggle
                    defaultToggled={getBool(element.value)}
                    style={{ paddingTop: 10 }}
                    onToggle={isInputChecked => {
                        // Update the value of element.
                        element.value = isInputChecked;
                        this.forceUpdate(this._onStructureChange);
                    }}
                />
            case FieldsItemType.Select:
                return <Select
                    key={'field-select-' + element.id}
                    isMulti={false}
                    isClearable={false}
                    containerStyle={{ width: '100%', margin: '0 4px' }}
                    options={this._renderSelectFieldItems(element)}
                    valueStr={element.value}
                    onChange={value => {
                        // Update the value of element.
                        element.value = value;
                        this.forceUpdate(this._onStructureChange);
                    }} />;
            case FieldsItemType.Color:
                return <Popover
                    outsideNode={
                        <div
                            style={{
                                padding: '5px',
                                background: '#fff',
                                borderRadius: '1px',
                                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                                display: 'inline-block',
                                cursor: 'pointer',
                                marginTop: 2
                            }}>
                            <div style={{
                                width: '36px',
                                height: '14px',
                                borderRadius: '2px',
                                background: element.value
                            }} />
                        </div>
                    }
                    boxNode={
                        <SketchPicker
                            disableAlpha={false}
                            color={isDefined(element.value) ? element.value : ""}
                            onChange={color => {
                                // Update the value of element.
                                element.value = "rgba("
                                    + color.rgb.r + ","
                                    + color.rgb.g + ","
                                    + color.rgb.b + ","
                                    + color.rgb.a + ")";
                                this.forceUpdate(this._onStructureChange);
                            }} />
                    } />;

            case FieldsItemType.Date:
                switch (element.operator) {
                    case "=":
                        let date = (!IsNullOrWhiteSpace(element.value)) ? moment(element.value).toDate() : null;
                        return <DatePicker
                            editable={false}
                            style={{
                                marginTop: 4,
                                borderRadius: 2,
                                backgroundColor: 'transparent'
                            }}
                            format='yyyy-MM-dd'
                            value={date}
                            onChange={(data) => {
                                // Update the value of element.
                                element.value = data?.toISOString() ?? null;
                                this.forceUpdate(this._onStructureChange);
                            }}
                            cleanable={false}
                            placement="auto"
                        />;
                    case "range":
                        let startDate;
                        let endDate;
                        if (isDefined(element.value)) {
                            const val = element.value.split(" - ");
                            startDate = new Date(val[0]);
                            endDate = new Date(val[1]);
                        } else
                            startDate = endDate = QueryBuilder._now;

                        const timePicker = this.props.TimePickerEnable;
                        return <RsDateRangePicker
                            localizationKey={this.props.localizationKey}
                            hasTime={timePicker}
                            value={[new Date(startDate), new Date(endDate)]}
                            onChange={undefined}
                            onChangeBackwardCompatible={(start, end) => {
                                // Update the value of element.
                                element.value = Formatters.datetimeRange(start, end, DateFormat.ISO, timePicker);
                                this.forceUpdate(this._onStructureChange);
                            }}>
                        </RsDateRangePicker>;
                }
        }
        return null;
    }
    // ---------------------------------------------------------------------------------------------------
    private _getQueryText(children: QueryStructure[], combinator: string, text: string): string {
        let hideCombinator: boolean = false;
        _.forEach(children, (v, i) => {

            if (v.type == QueryItemType.Group) {
                if (_.isArray(v.rules) && v.rules.length > 0)
                    if (i == 0) {
                        if (v.notEnabled)
                            text += `not (${this._getQueryText(v.rules, v.combinator, '')}) `;
                        else
                            text += `(${this._getQueryText(v.rules, v.combinator, '')}) `;
                    }
                    else {
                        if (v.notEnabled)
                            text += ` ${combinator} not (${this._getQueryText(v.rules, v.combinator, '')}) `;
                        else
                            text += ` ${combinator} (${this._getQueryText(v.rules, v.combinator, '')}) `;
                    }
            }
            else {
                const field = this._fieldsCache[v.field];
                if (!isDefined(field))
                    return;

                let type: FieldsItemType = field.type;
                if (!isDefined(v))
                    return;

                if (i == 0 || hideCombinator) {
                    if (type == FieldsItemType.Date && v.operator == "range") {
                        if (isDefined(v.value)) {
                            const spliVal = v.value.split(" - ");
                            text += `(${v.field} >= "${spliVal[0]}" and ${v.field} <= "${spliVal[1]}")`;
                            hideCombinator = false;
                        }
                        else {
                            hideCombinator = true;
                        }
                    }
                    else {
                        text += `(${v.field} ${v.operator}`;
                        if (!this._isNullOperator(v.operator)) {
                            text += ` ${this.getQueryValue(v)}`;
                        }
                        text += ") ";
                        hideCombinator = false;
                    }
                } else {
                    if (type == FieldsItemType.Date && v.operator == "range") {
                        if (isDefined(v.value)) {
                            const spliVal = v.value.split(" - ");
                            text += `(${v.field} >= "${spliVal[0]}" and ${v.field} <= "${spliVal[1]}")`;
                        }
                    }
                    else {
                        text += ` ${combinator} (${v.field} ${v.operator}`;
                        if (!this._isNullOperator(v.operator)) {
                            text += ` ${this.getQueryValue(v)}`;
                        }
                        text += ") ";
                    }
                }
            }
            text = _.trimEnd(text);
        });
        return text;
    }
    // ---------------------------------------------------------------------------------------------------
    private getQueryValue(element: QueryStructure): string {
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return 'null';
        switch (field.type) {
            case FieldsItemType.Boolean:
            case FieldsItemType.Number:
                return element.value;
            case FieldsItemType.Color:
            case FieldsItemType.Date:
            case FieldsItemType.Select:
            case FieldsItemType.Text:
            default:
                return `"${element.value}"`;
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _onSendOutClick = () => {
        this._updateQueryText();

        const { updateOutputPort } = this.props;
        if (isFunction(updateOutputPort))
            updateOutputPort(this._query, this.state.QueryStructure);
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateQueryText = () => {
        const { QueryStructure } = this.state;
        this._query = this._getQueryText(QueryStructure, QueryStructure[0].combinator, "");
    }
    // ---------------------------------------------------------------------------------------------------
    private _onStructureChange = () => {
        // use `onQueryStructureChange` callback
        const { onQueryStructureChange, HideToolbar } = this.props;
        // update widget
        if (isFunction(onQueryStructureChange))
            onQueryStructureChange(this.state.QueryStructure);

        // if no toolbar is shown; send event
        if (HideToolbar)
            this._onSendOutClick();
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key.
     */
    public static LocalizeDictionary(key: string): any {
        // get default values
        const defaultValues = GetQueryBuilderLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(key)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const loc = Localization.GetInstance(key);

        if (!isDefined(loc)) {
            DebugEx.TraceError("QueryBuilder: passed `localizationKey`: " + key + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = loc.GetTextMap(QueryBuilderLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("QueryBuilder: passed `localizationKey`: " + key + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------
}